
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  UtensilsCrossed, 
  ChefHat, 
  BarChart2, 
  TrendingUp, 
  Zap, 
  DollarSign, 
  ShoppingCart, 
  LucideIcon
} from "lucide-react";
import { motion } from "@/components/ui/motion";

// Feature card component
interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  delay: number;
}

const FeatureCard = ({ title, description, icon: Icon, delay }: FeatureCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
  >
    <Card className="h-full border-t-4 border-t-primary hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <div className="p-2 rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-base">{description}</CardDescription>
      </CardContent>
    </Card>
  </motion.div>
);

// Testimonial component
interface TestimonialProps {
  quote: string;
  author: string;
  role: string;
  delay: number;
}

const Testimonial = ({ quote, author, role, delay }: TestimonialProps) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    className="h-full"
  >
    <Card className="h-full flex flex-col border-none bg-secondary/50 shadow-md">
      <CardContent className="flex-grow flex flex-col justify-between pt-6">
        <div>
          <p className="text-lg italic mb-4 text-foreground">"{quote}"</p>
        </div>
        <div>
          <p className="font-semibold text-foreground">{author}</p>
          <p className="text-sm text-muted-foreground">{role}</p>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const LandingPage = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  // Features data mapped to translations
  const features = [
    {
      title: t('landing.features.menuEngineering.title'),
      description: t('landing.features.menuEngineering.description'),
      icon: BarChart2
    },
    {
      title: t('landing.features.inventoryIntelligence.title'),
      description: t('landing.features.inventoryIntelligence.description'),
      icon: ShoppingCart
    },
    {
      title: t('landing.features.dynamicPricing.title'),
      description: t('landing.features.dynamicPricing.description'),
      icon: DollarSign
    },
    {
      title: t('landing.features.aiInsights.title'),
      description: t('landing.features.aiInsights.description'),
      icon: Zap
    },
    {
      title: t('landing.features.recipeInnovation.title'),
      description: t('landing.features.recipeInnovation.description'),
      icon: ChefHat
    },
    {
      title: t('landing.features.financialForecasting.title'),
      description: t('landing.features.financialForecasting.description'),
      icon: TrendingUp
    }
  ];

  // Testimonials data mapped to translations
  const testimonials = [
    {
      quote: t('landing.testimonials.testimonial1.quote'),
      author: t('landing.testimonials.testimonial1.author'),
      role: t('landing.testimonials.testimonial1.role')
    },
    {
      quote: t('landing.testimonials.testimonial2.quote'),
      author: t('landing.testimonials.testimonial2.author'),
      role: t('landing.testimonials.testimonial2.role')
    },
    {
      quote: t('landing.testimonials.testimonial3.quote'),
      author: t('landing.testimonials.testimonial3.author'),
      role: t('landing.testimonials.testimonial3.role')
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero section */}
      <section className="bg-gradient-to-b from-background to-muted/30 pt-12 pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
            {/* Hero content */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 space-y-6"
            >
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm text-primary">
                <span className="mr-2">✨</span> {t('landing.hero.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                {t('landing.hero.title')}
              </h1>
              <p className="text-xl text-muted-foreground">
                {t('landing.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/login">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {t('landing.hero.getStarted')}
                    <motion.span 
                      animate={{ x: isHovered ? 5 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      →
                    </motion.span>
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    {t('landing.hero.signUp')}
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            {/* Hero image/visualization */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 mt-8 md:mt-0"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl blur-3xl opacity-30"></div>
                <div className="relative bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
                  <div className="p-1">
                    <div className="flex items-center gap-2 p-2 border-b border-border">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-destructive/80"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                      </div>
                    </div>
                    <div className="bg-card p-4">
                      <div className="space-y-4">
                        <div className="h-8 flex items-center">
                          <UtensilsCrossed className="h-6 w-6 text-primary mr-2" /> 
                          <span className="font-medium">MrCook Dashboard</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-secondary/40 h-24 rounded-md flex items-center justify-center">
                            <BarChart2 className="h-8 w-8 text-primary/70" />
                          </div>
                          <div className="bg-secondary/40 h-24 rounded-md flex items-center justify-center">
                            <TrendingUp className="h-8 w-8 text-primary/70" />
                          </div>
                          <div className="bg-secondary/40 h-24 rounded-md flex items-center justify-center">
                            <DollarSign className="h-8 w-8 text-primary/70" />
                          </div>
                          <div className="bg-secondary/40 h-24 rounded-md flex items-center justify-center">
                            <Zap className="h-8 w-8 text-primary/70" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">{t('landing.features.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                delay={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials section */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">{t('landing.testimonials.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Testimonial 
                key={index}
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
                delay={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-16 bg-primary/10">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <h2 className="text-3xl font-bold">{t('landing.cta.title')}</h2>
            <p className="text-lg text-muted-foreground">
              {t('landing.cta.subtitle')}
            </p>
            <div className="pt-4">
              <Link to="/register">
                <Button size="lg" className="px-8">
                  {t('landing.cta.button')}
                </Button>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground pt-4">
              {t('landing.cta.noCreditCard')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-6 md:mb-0">
              <UtensilsCrossed className="h-6 w-6 text-primary mr-2" />
              <span className="text-xl font-bold">MrCook</span>
            </div>
            <div className="flex gap-6">
              <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('landing.footer.login')}
              </Link>
              <Link to="/register" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('landing.footer.register')}
              </Link>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('landing.footer.privacy')}
              </a>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('landing.footer.terms')}
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>{t('landing.footer.rights', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
